import { useState } from "react";
import { IconButton, Typography, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
    ArrowForward,
    DownloadOutlined,
    DarkMode,
    LightMode,
    LockOutlined,
    BoltOutlined,
    DarkModeOutlined,
    LinkOutlined,
    NoteAddOutlined,
} from "@mui/icons-material";

import "./index.scss";
import { useTheme } from "../../hooks";

//@ts-ignore
import { ReactComponent as Logo } from "../../assets/images/logo_v2.svg";
import { NavLink } from "react-router-dom";

export default function NotepadHome() {
    const navigate = useNavigate();
    const [retrieveCode, setRetrieveCode] = useState("");
    const [activeTab, setActiveTab] = useState<"create" | "retrieve">("create");
    const [idType, setIdType] = useState<"simple" | "complex">("simple");
    const { mode, setMode } = useTheme();

    const handleCreate = () => {
        const uniqueString = idType === "simple" ? generateSimpleId() : generateUniqueString();
        navigate(`/notedrop/${uniqueString}`, { state: { newPage: true } });
    };

    function generateSimpleId() {
        return Math.floor(100000 + Math.random() * 900000).toString();
    }

    const handleRetrieve = () => {
        if (retrieveCode.trim()) {
            navigate(`/notedrop/${retrieveCode}?fetch=true`);
        }
    };

    function generateUniqueString() {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substr(2, 5);
        const uniqueString = timestamp + random;
        let machineIdentifier = "";
        for (let i = 0; i < uniqueString.length; i++) {
            if (i > 0 && i % 7 === 0) {
                machineIdentifier += "-";
            }
            machineIdentifier += uniqueString[i];
        }
        return machineIdentifier;
    }

    const changeTheme = () => {
        setMode((current: any) => (current === "dark" ? "light" : "dark"));
    };

    return (
        <div className="hero-landing">
            {/* Floating doodles */}
            <div className="doodle doodle-star" aria-hidden="true">
                ✦
            </div>
            <div className="doodle doodle-circle" aria-hidden="true">
                ◯
            </div>
            <div className="doodle doodle-squiggle" aria-hidden="true">
                〰
            </div>
            <div className="doodle doodle-arrow" aria-hidden="true">
                ↗
            </div>
            <div className="doodle doodle-dot" aria-hidden="true">
                ●
            </div>
            <div className="doodle doodle-cross" aria-hidden="true">
                ✕
            </div>

            {/* Minimal nav */}
            <nav className="hero-nav">
                <NavLink to="/notedrop" className="hero-logo-link">
                    <Logo
                        style={{
                            width: 34,
                            height: 34,
                            filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.1))",
                        }}
                    />
                    <Stack spacing={0}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: "var(--fq-font-family)",
                                fontWeight: 800,
                                fontSize: "1.15rem",
                                letterSpacing: "-0.02em",
                                color: "var(--fq-primary-text-color)",
                                lineHeight: 1.1,
                            }}
                        >
                            NoteDrop
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                color: "var(--fq-secondary-text-color)",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                opacity: 0.8,
                            }}
                        >
                            nithinpb.com product
                        </Typography>
                    </Stack>
                </NavLink>
                <IconButton onClick={changeTheme} className="theme-toggle-btn" size="small">
                    {mode === "dark" ? (
                        <DarkMode fontSize="small" sx={{ color: "var(--fq-primary-text-color)" }} />
                    ) : (
                        <LightMode fontSize="small" sx={{ color: "var(--fq-primary-text-color)" }} />
                    )}
                </IconButton>
            </nav>

            {/* Hero split layout: left text + right action card */}
            <main className="hero-main">
                <div className="hero-left">
                    <h1 className="hero-title">
                        <span className="hero-title-line1">Drop your</span>
                        <span className="hero-title-line2">
                            notes <span className="hero-highlight sketchy-border">here</span>
                        </span>
                    </h1>
                    <p className="hero-subtitle">
                        the quickest way to share encrypted notes.
                        <br />
                        no signup. no bs. just drop it & grab it.
                    </p>

                    {/* Feature pills inline */}
                    <div className="feature-pills">
                        <span className="pill sketchy-border">
                            <LockOutlined sx={{ fontSize: 16 }} /> encrypted
                        </span>
                        <span className="pill sketchy-border">
                            <BoltOutlined sx={{ fontSize: 16 }} /> instant
                        </span>
                        <span className="pill sketchy-border">
                            <DarkModeOutlined sx={{ fontSize: 16 }} /> dark mode
                        </span>
                        <span className="pill sketchy-border">
                            <LinkOutlined sx={{ fontSize: 16 }} /> shareable
                        </span>
                    </div>
                </div>

                <div className="hero-right">
                    {/* Action card */}
                    <div className="hero-action-card sketchy-border-alt">
                        <div className="action-tabs">
                            <button
                                className={`action-tab ${activeTab === "create" ? "active" : ""}`}
                                onClick={() => setActiveTab("create")}
                            >
                                <NoteAddOutlined sx={{ fontSize: 20, mr: 0.5 }} /> new note
                            </button>
                            <button
                                className={`action-tab ${activeTab === "retrieve" ? "active" : ""}`}
                                onClick={() => setActiveTab("retrieve")}
                            >
                                <DownloadOutlined sx={{ fontSize: 20, mr: 0.5 }} /> grab note
                            </button>
                        </div>

                        {activeTab === "create" && (
                            <div className="action-panel">
                                <p className="action-description">
                                    Start a fresh note — encrypted, shareable, and ready to go. No signup needed!
                                </p>
                                <div className="id-type-selector">
                                    <button
                                        className={`id-type-btn ${idType === "simple" ? "active" : ""}`}
                                        onClick={() => setIdType("simple")}
                                    >
                                        <div className="id-type-label">Simple ID</div>
                                        <div className="id-type-example">e.g. 123456</div>
                                    </button>
                                    <button
                                        className={`id-type-btn ${idType === "complex" ? "active" : ""}`}
                                        onClick={() => setIdType("complex")}
                                    >
                                        <div className="id-type-label">Unique ID</div>
                                        <div className="id-type-example">e.g. k9-x2-m1</div>
                                    </button>
                                </div>
                                <button className="action-button create-btn sketchy-border" onClick={handleCreate}>
                                    <span>create a note</span>
                                    <ArrowForward sx={{ fontSize: 20 }} />
                                </button>
                            </div>
                        )}

                        {activeTab === "retrieve" && (
                            <div className="action-panel">
                                <p className="action-description">
                                    Got a code? Paste it below and we'll fetch your note.
                                </p>
                                <input
                                    className="retrieve-input sketchy-border-soft"
                                    placeholder="paste your code here..."
                                    value={retrieveCode}
                                    onChange={(e) => setRetrieveCode(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleRetrieve()}
                                />
                                <button
                                    className="action-button retrieve-btn sketchy-border"
                                    onClick={handleRetrieve}
                                    disabled={!retrieveCode.trim()}
                                >
                                    <DownloadOutlined sx={{ fontSize: 20 }} />
                                    <span>grab it</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
